import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import {
  AlertCircle,
  Building2,
  FileCheck,
  Globe,
  Loader2,
  Mail,
  MapPin,
  Phone,
  Truck,
  User,
  ShieldCheck,
  PenSquare
} from "lucide-react";
import { CollapsibleSection } from "src/components/ui/collapsible";
import { getInstanceOfVendorService } from "src/dependencies";
import { useVendorById } from "src/hooks";
import { VendorAddress } from "src/types";
import { RoleHelper } from "src/helpers";
import { useAuthorisation } from "src/context";
import { ROUTES_CONFIG } from "src/constants/routesConfig";
import { INCO_TERMS_OPTIONS, COUNTRY_OF_EXPORT_OPTIONS } from "src/constants";
import { Incoterms } from "src/enums/incoterms";

interface PurchaseOrderVendorSectionProps {
  vendorId?: string;
  initiallyOpen?: boolean;
  withCollapsible?: boolean;
}

const PurchaseOrderVendorSection: React.FC<PurchaseOrderVendorSectionProps> = ({
  vendorId,
  initiallyOpen = false,
  withCollapsible = true
}) => {
  const vendorService = useMemo(() => getInstanceOfVendorService(), []);
  const { userRole } = useAuthorisation();
  const { vendor, error, isLoading, isFetching, refetch } = useVendorById(vendorId, vendorService);
  if (!vendorId) {
    return null;
  }
  const canViewOpsDetails = RoleHelper.isHealfOperationsTeam(userRole);

  const renderStatusPill = (label: React.ReactNode, variant: "success" | "warning" | "info") => {
    const variantClasses =
      variant === "success"
        ? "border border-success-200 bg-success-50 text-success-700"
        : variant === "warning"
          ? "border border-warning-200 bg-warning-50 text-warning-700"
          : "border border-primary-200 bg-primary-50 text-primary-700";

    return (
      <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${variantClasses}`}>
        {label}
      </span>
    );
  };

  const renderAddress = (address: VendorAddress, index: number) => {
    const street = [address.line1, address.line2, address.line3].filter(Boolean).join(", ");
    const cityLine = [address.city, address.country, address.zipCode].filter(Boolean).join(", ");

    return (
      <div
        key={`${address.line1}-${address.zipCode}-${index}`}
        className="rounded-xl border border-secondary-100 bg-secondary-25 p-4 space-y-1"
      >
        {street && <p className="text-sm text-secondary-700">{street}</p>}
        {cityLine && <p className="text-sm text-secondary-700">{cityLine}</p>}
        {address.countryCode && <p className="text-xs text-secondary-500">Country code: {address.countryCode}</p>}
      </div>
    );
  };

  const renderContent = () => {
    if (!vendorId) {
      return (
        <div className="rounded-xl border border-secondary-200 bg-secondary-50 p-5 text-sm text-secondary-700">
          No vendor has been linked to this record yet.
        </div>
      );
    }

    if (isLoading) {
      return (
        <div className="flex flex-col items-center justify-center py-8 text-secondary-600">
          <Loader2 className="mb-3 h-6 w-6 animate-spin text-primary-600" />
          <p className="text-sm font-medium">Loading vendor details…</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="rounded-xl border border-error-200 bg-error-50 p-5">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-error-600" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-error-700">Failed to load vendor information</p>
              <p className="text-sm text-error-600">
                {error instanceof Error ? error.message : "Unexpected error while fetching vendor details."}
              </p>
              <button
                type="button"
                onClick={() => refetch()}
                className="mt-3 inline-flex items-center rounded-md bg-error-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-error-700 focus:outline-none focus:ring-2 focus:ring-error-500 focus:ring-offset-2"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      );
    }

    if (!vendor) {
      return (
        <div className="rounded-xl border border-secondary-200 bg-secondary-50 p-5 text-sm text-secondary-700">
          Vendor details are unavailable right now.
        </div>
      );
    }

    const shipmentDetails = vendor.shipmentDetails;
    const currentAddresses = shipmentDetails?.addresses?.filter((address) => address.isCurrent) ?? [];

    const statusBadges = [
      vendor.portalOrderVendor ? renderStatusPill("Portal Order", "success") : null,
      vendor.collectionOrderVendor ? renderStatusPill("Collection Vendor", "info") : null,
      vendor.isProForma ? renderStatusPill("Pro Forma", "warning") : null,
      isFetching && !isLoading
        ? renderStatusPill(
            <>
              <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
              Syncing
            </>,
            "info"
          )
        : null
    ].reduce<React.ReactNode[]>((acc, badge) => {
      if (badge) {
        acc.push(badge);
      }
      return acc;
    }, []);

    const getIncoTermsParts = (value?: string): { code: string; description: string } => {
      const label =
        value != null
          ? INCO_TERMS_OPTIONS.find((opt) => opt.value === value)?.label
          : INCO_TERMS_OPTIONS.find((opt) => opt.value === Incoterms.EXW)?.label;
      if (!label) return { code: value ?? Incoterms.EXW, description: "" };
      const dashIndex = label.indexOf(" - ");
      if (dashIndex === -1) return { code: label, description: "" };
      return {
        code: label.slice(0, dashIndex).trim(),
        description: label.slice(dashIndex + 3).trim()
      };
    };

    const keyFacts = [
      {
        label: "Portal Orders",
        value: vendor.portalOrderVendor ? "Yes" : "No"
      },
      {
        label: "Collection Flow",
        value: vendor.collectionOrderVendor ? "Yes" : "No"
      },
      vendor.isProForma
        ? {
            label: "Pro Forma",
            value: "Yes"
          }
        : null,
      {
        label: "Portal Access",
        value: vendor.portalAccess ? "Yes" : "No"
      },
      {
        label: "Preferred File Type",
        value: vendor.fileType ?? "PDF"
      },
      { label: "Inco Terms", value: "", kind: "incoTerms" as const },
      { label: "Country of Export", value: "", kind: "countryOfExport" as const }
    ].filter(Boolean) as Array<{ label: string; value: string; kind?: "incoTerms" | "countryOfExport" }>;

    return (
      <div className="space-y-5">
        <div className="rounded-2xl border border-secondary-200 bg-gradient-to-r from-primary-50 via-white to-secondary-50 p-6 shadow-soft">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="rounded-2xl bg-white/80 p-3 shadow-inner">
                <Building2 className="h-6 w-6 text-primary-600" />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-secondary-500">Vendor</p>
                <h4 className="text-2xl font-semibold text-secondary-900">{vendor.name}</h4>
                {vendor.location && (
                  <div className="mt-3 flex items-center text-sm text-secondary-600">
                    <Globe className="mr-2 h-4 w-4 text-primary-600" />
                    <span>
                      Origin: <span className="font-medium text-secondary-900">{vendor.location}</span>
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-col items-end gap-3">
              <div className="flex flex-wrap gap-2 justify-end">{statusBadges}</div>
              {canViewOpsDetails && vendor.name && (
                <Link
                  to={`${ROUTES_CONFIG.updateVendor.path}?name=${encodeURIComponent(vendor.name)}`}
                  className="inline-flex items-center gap-2 rounded-full bg-secondary-900 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-secondary-800 focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:ring-offset-2"
                >
                  <PenSquare className="h-4 w-4" />
                  Update Vendor
                </Link>
              )}
            </div>
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {keyFacts.map((fact) => (
            <div key={fact.label} className="rounded-2xl border border-secondary-100 bg-white p-4 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-wide text-secondary-500">{fact.label}</p>
              {fact.kind === "incoTerms" ? (
                (() => {
                  const { code, description } = getIncoTermsParts(vendor.incoTerms);
                  return (
                    <div className="mt-1.5 flex flex-col gap-1.5">
                      <span className="inline-flex w-fit rounded-md bg-primary-100 px-2.5 py-1 text-sm font-semibold text-primary-800 ring-1 ring-primary-200/60">
                        {code}
                      </span>
                      {description && (
                        <p className="text-xs text-secondary-600 leading-snug line-clamp-2" title={description}>
                          {description}
                        </p>
                      )}
                    </div>
                  );
                })()
              ) : fact.kind === "countryOfExport" ? (
                (() => {
                  const codes = vendor.countryOfExport;
                  if (codes == null || !codes.length) {
                    return (
                      <div className="mt-1.5 flex items-center gap-2 min-w-0">
                        <span className="inline-flex shrink-0 rounded-md bg-secondary-100 px-2 py-0.5 text-xs font-semibold text-secondary-500 ring-1 ring-secondary-200/60">
                          N/A
                        </span>
                        <span className="text-xs text-secondary-500">Not set</span>
                      </div>
                    );
                  }
                  return (
                    <div className="mt-1.5 flex flex-col gap-1.5">
                      {codes.map((code) => {
                        const opt = COUNTRY_OF_EXPORT_OPTIONS.find((o) => o.value === code);
                        const dashIdx = opt?.label?.indexOf(" - ");
                        const name =
                          opt && dashIdx !== undefined && dashIdx >= 0 ? opt.label.slice(dashIdx + 3).trim() : code;
                        return (
                          <div key={code} className="flex items-center gap-2 min-w-0" title={opt?.label ?? code}>
                            <span className="inline-flex shrink-0 rounded-md bg-secondary-100 px-2 py-0.5 text-xs font-semibold text-secondary-800 ring-1 ring-secondary-200/60">
                              {code}
                            </span>
                            <span className="text-xs text-secondary-700 truncate">{name}</span>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()
              ) : (
                <p className="text-xl font-semibold text-secondary-900">{fact.value}</p>
              )}
            </div>
          ))}
        </div>

        <div className="rounded-2xl border border-secondary-200 bg-white p-5 shadow-soft">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <Mail className="h-5 w-5 text-primary-600" />
              <div>
                <p className="text-sm font-semibold text-secondary-900">Vendor Emails</p>
                <p className="text-xs text-secondary-500">Primary recipients for purchase orders</p>
              </div>
            </div>
            <span className="text-xs font-semibold text-secondary-500">
              {vendor.emails?.length
                ? `${vendor.emails.length} contact${vendor.emails.length > 1 ? "s" : ""}`
                : "No contacts"}
            </span>
          </div>
          {vendor.emails?.length ? (
            <div className="mt-4 flex flex-wrap gap-2">
              {vendor.emails.map((email) => (
                <span
                  key={email}
                  className="inline-flex items-center rounded-md border border-primary-200 bg-primary-50 px-3 py-1 text-xs font-medium text-primary-800"
                >
                  {email}
                </span>
              ))}
            </div>
          ) : (
            <p className="mt-4 text-sm text-secondary-600">No vendor emails recorded.</p>
          )}
        </div>

        {(vendor.collectionOrderVendor || shipmentDetails) && (
          <div className="rounded-2xl border border-secondary-200 bg-white p-5 shadow-soft space-y-4">
            <div className="flex items-center gap-3">
              <Truck className="h-5 w-5 text-primary-600" />
              <div>
                <p className="text-sm font-semibold text-secondary-900">Collection & Shipment Details</p>
                <p className="text-xs text-secondary-500">Displayed for collection vendors</p>
              </div>
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
              <div className="rounded-2xl border border-secondary-100 bg-secondary-25 p-4 space-y-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-secondary-500">Contact Information</p>
                {shipmentDetails?.contactPerson && (
                  <div className="flex items-center text-sm text-secondary-800">
                    <User className="mr-2 h-4 w-4 text-primary-600" />
                    {shipmentDetails.contactPerson}
                  </div>
                )}
                {shipmentDetails?.contactInfo && (
                  <div className="flex items-center text-sm text-secondary-800">
                    <Phone className="mr-2 h-4 w-4 text-primary-600" />
                    {shipmentDetails.contactInfo}
                  </div>
                )}
                {shipmentDetails?.email && (
                  <div className="flex items-center text-sm text-secondary-800">
                    <Mail className="mr-2 h-4 w-4 text-primary-600" />
                    {shipmentDetails.email}
                  </div>
                )}
              </div>
              <div className="rounded-2xl border border-secondary-100 bg-secondary-25 p-4 space-y-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-secondary-500 flex items-center">
                  <MapPin className="mr-2 h-4 w-4 text-primary-600" />
                  Current Collection Address
                </p>
                {currentAddresses.length ? (
                  <div className="grid gap-3 md:grid-cols-2">{currentAddresses.map(renderAddress)}</div>
                ) : (
                  <p className="text-sm text-secondary-700">No current collection address recorded.</p>
                )}
              </div>
            </div>

            {canViewOpsDetails &&
              (shipmentDetails?.collection ||
                typeof shipmentDetails?.ciRequired === "boolean" ||
                typeof shipmentDetails?.bolRequired === "boolean" ||
                shipmentDetails?.ein ||
                shipmentDetails?.opsPOC ||
                shipmentDetails?.notes) && (
                <div className="rounded-2xl border border-secondary-100 bg-secondary-25 p-4 space-y-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-secondary-500">Operations Only</p>
                  {shipmentDetails?.collection && (
                    <div className="text-sm text-secondary-800">
                      <p className="font-semibold">Collection Instructions</p>
                      <p className="mt-1">{shipmentDetails.collection}</p>
                    </div>
                  )}
                  {typeof shipmentDetails?.ciRequired === "boolean" && (
                    <div className="flex items-center text-sm text-secondary-800">
                      <FileCheck className="mr-2 h-4 w-4 text-primary-600" />
                      Commercial Invoice: {shipmentDetails.ciRequired ? "Required" : "Not required"}
                    </div>
                  )}
                  {typeof shipmentDetails?.bolRequired === "boolean" && (
                    <div className="flex items-center text-sm text-secondary-800">
                      <FileCheck className="mr-2 h-4 w-4 text-primary-600" />
                      Bill of Lading: {shipmentDetails.bolRequired ? "Required" : "Not required"}
                    </div>
                  )}
                  {shipmentDetails?.ein && (
                    <div className="flex items-center text-sm text-secondary-800">
                      <ShieldCheck className="mr-2 h-4 w-4 text-primary-600" />
                      EIN: {shipmentDetails.ein}
                    </div>
                  )}
                  {shipmentDetails?.opsPOC && (
                    <div className="flex items-center text-sm text-secondary-800">
                      <Building2 className="mr-2 h-4 w-4 text-primary-600" />
                      Ops POC: {shipmentDetails.opsPOC}
                    </div>
                  )}
                  {shipmentDetails?.notes && (
                    <p className="text-sm text-secondary-700 whitespace-pre-line">Notes: {shipmentDetails.notes}</p>
                  )}
                </div>
              )}
          </div>
        )}
      </div>
    );
  };

  const content = <div className="p-6 space-y-5">{renderContent()}</div>;

  if (!withCollapsible) {
    return (
      <section className="rounded-2xl border border-secondary-200 bg-white shadow-soft">
        <div className="flex items-center gap-2 border-b border-secondary-100 px-6 py-4">
          <Building2 className="h-5 w-5 text-primary-600" />
          <h3 className="text-base font-semibold text-secondary-900">Vendor Information</h3>
        </div>
        {content}
      </section>
    );
  }

  return (
    <div className="bg-white border border-secondary-200 rounded-xl shadow-soft overflow-hidden">
      <CollapsibleSection
        initiallyOpen={initiallyOpen}
        title={
          <h3 className="text-lg font-semibold text-secondary-800 flex items-center">
            <Building2 className="w-5 h-5 mr-2 text-primary-600" />
            Vendor Information
          </h3>
        }
      >
        {content}
      </CollapsibleSection>
    </div>
  );
};

export { PurchaseOrderVendorSection };
